import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceSchedule} from '@/entities/personnel/types';
import {activeWeekdays, formatDayMinutes} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {Card} from '@/shared/ui/card';
import {Explain, type Definition} from '@/shared/ui/explain';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * The schedule a report's verdicts were computed against.
 *
 * This is not decoration. Every "опоздание" and every "отсутствие" in the numbers
 * above it is a comparison against exactly these values, and the single most common
 * question a manager asks of an attendance report — «почему у него опоздание, он
 * пришёл в 9:05» — is answered by the grace window shown here. A report that states
 * its counters without stating the rule they came from invites the reader to assume a
 * rule that is not the one in force.
 *
 * A null schedule is itself the answer, and an important one: with no schedule the
 * engine cannot call anything late or absent, so every day lands in
 * `unscheduled_days` and the punctuality figures above are structurally zero.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(2)},
  headText: {flex: 1},
  hours: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  days: {flexDirection: 'row', gap: spacing(1), flexWrap: 'wrap'},
  day: {
    minWidth: 34,
    paddingVertical: spacing(1),
    paddingHorizontal: spacing(2),
    borderRadius: radii.sm,
    alignItems: 'center',
  },
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  cell: {minWidth: 120, gap: 1},
});

export function ScheduleSummary({
  schedule,
  /** Where the schedule came from — "personnel" / "department" / null. */
  source,
}: {
  schedule: AttendanceSchedule | null;
  source?: string | null;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  if (!schedule) {
    // Not a caption in a quiet card: with no schedule, half the counters above are
    // structurally zero and the two rates are em dashes. That is the most consequential
    // fact on the screen, and it is also fixable — so it is a banner that says where.
    return (
      <Banner
        tone="warning"
        title={t('reports.noScheduleTitle')}
        message={t('reports.noSchedule')}
        testID="schedule-summary-none"
      />
    );
  }

  const start = formatDayMinutes(schedule.start_minutes);
  const end = formatDayMinutes(schedule.end_minutes);
  const workdays = new Set(activeWeekdays(schedule.workdays_mask));

  /** The four thresholds, each in the words the counters above are stated in. */
  const definitions: Definition[] = [
    'lateGrace',
    'earlyArrivalThreshold',
    'earlyLeaveGrace',
    'overtimeThreshold',
    'workdays',
  ].map(id => ({
    term: t(`reports.def.${id}.term`),
    meaning: t(`reports.def.${id}.meaning`),
  }));

  const cell = (label: string, value: string) => (
    <View style={styles.cell} key={label}>
      <AppText variant="mono">{value}</AppText>
      <AppText variant="micro" numberOfLines={2}>
        {label}
      </AppText>
    </View>
  );

  return (
    <Card padding={4} style={styles.root} testID="schedule-summary">
      <View style={styles.head}>
        <View style={styles.headText}>
          <AppText variant="heading">{t('reports.schedule')}</AppText>
          <AppText variant="caption">
            {source
              ? t('reports.scheduleSource', {
                  source: t(`reports.source.${source}`, source),
                })
              : t('reports.scheduleHint')}
          </AppText>
        </View>
        <Explain
          testID="explain-schedule"
          title={t('reports.schedule')}
          subtitle={t('reports.scheduleHint')}
          definitions={definitions}
        />
      </View>

      <View style={styles.hours}>
        <AppText variant="monoLg" color={start && end ? p.foreground : p.mutedForeground}>
          {start && end ? `${start} — ${end}` : t('reports.openHours')}
        </AppText>
      </View>

      <View style={styles.days}>
        {/* Mon-first, matching sv_control's own `weekday_bit` order. */}
        {[0, 1, 2, 3, 4, 5, 6].map(index => {
          const on = workdays.has(index);
          return (
            <View
              key={index}
              style={[styles.day, {backgroundColor: on ? p.accent : p.muted}]}>
              <AppText variant="micro" color={on ? p.accentForeground : p.mutedForeground}>
                {t(`attendance.weekday.${index + 1}`)}
              </AppText>
            </View>
          );
        })}
      </View>

      <View style={styles.grid}>
        {cell(
          t('reports.lateGrace'),
          t('reports.minutes', {count: schedule.late_grace_minutes}),
        )}
        {cell(
          t('reports.earlyArrivalThreshold'),
          t('reports.minutes', {count: schedule.early_arrival_threshold_minutes}),
        )}
        {cell(
          t('reports.earlyLeaveGrace'),
          t('reports.minutes', {count: schedule.early_leave_grace_minutes}),
        )}
        {cell(
          t('reports.overtimeThreshold'),
          t('reports.minutes', {count: schedule.overtime_threshold_minutes}),
        )}
      </View>
    </Card>
  );
}
